import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { View, KeyboardAvoidingView, Platform, useWindowDimensions } from 'react-native';
import { signInStyles } from '../styles/signIn.styles';
import {
  AUTH_SCREEN_LARGE_MIN_WIDTH,
  AUTH_SCREEN_LARGE_CENTER_MIN_HEIGHT,
  AUTH_SCREEN_COLUMN_MAX_WIDTH_COMPACT,
  AUTH_SCREEN_COLUMN_MAX_WIDTH_LARGE,
  AUTH_SCREEN_EXTRA_TOP_PADDING,
  AUTH_SCREEN_PADDING_BOTTOM_COMPACT,
  AUTH_SCREEN_PADDING_BOTTOM_LARGE,
} from '../constants/signInLayout.constants';

type SignInAuthScreenLayoutProps = {
  children: ReactNode;
  testID?: string;
};

/**
 * Public auth screens: one full-viewport column (no inner scroll). Compact widths use a flex
 * spacer + auth block so the sheet stays bottom-weighted like `justifyContent: 'flex-end'`
 * without relying on paddingTop (which flex-end does not reserve above children). Web uses
 * `--app-dvh` on the auth shell (see signIn.styles) so height tracks the visible mobile viewport.
 */
export function SignInAuthScreenLayout({ children, testID }: SignInAuthScreenLayoutProps) {
  const { width, height } = useWindowDimensions();
  const isLarge = width >= AUTH_SCREEN_LARGE_MIN_WIDTH;
  const verticallyCenter =
    isLarge && height >= AUTH_SCREEN_LARGE_CENTER_MIN_HEIGHT;

  const largeFrameStyle = useMemo(
    () => ({
      flex: 1,
      minHeight: 0,
      justifyContent: verticallyCenter ? ('center' as const) : ('flex-end' as const),
      paddingHorizontal: 14,
      paddingTop: (isLarge ? 24 : 12) + AUTH_SCREEN_EXTRA_TOP_PADDING,
      paddingBottom: isLarge ? AUTH_SCREEN_PADDING_BOTTOM_LARGE : AUTH_SCREEN_PADDING_BOTTOM_COMPACT,
    }),
    [isLarge, verticallyCenter],
  );

  const compactFrameStyle = useMemo(
    () => ({
      flex: 1,
      minHeight: 0,
      flexDirection: 'column' as const,
      paddingHorizontal: 14,
      paddingTop: 8,
      paddingBottom: AUTH_SCREEN_PADDING_BOTTOM_COMPACT,
    }),
    [],
  );

  const columnStyle = useMemo(
    () => ({
      width: '100%' as const,
      maxWidth: isLarge ? AUTH_SCREEN_COLUMN_MAX_WIDTH_LARGE : AUTH_SCREEN_COLUMN_MAX_WIDTH_COMPACT,
      alignSelf: 'center' as const,
    }),
    [isLarge],
  );

  return (
    <View style={signInStyles.screenRoot} testID={testID}>
      <KeyboardAvoidingView
        style={signInStyles.scrollFill}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={0}
      >
        {isLarge ? (
          <View style={largeFrameStyle}>
            <View style={columnStyle}>{children}</View>
          </View>
        ) : (
          <View style={compactFrameStyle}>
            <View style={{ flexGrow: 1, flexShrink: 1, minHeight: 0 }} />
            <View style={columnStyle}>{children}</View>
          </View>
        )}
      </KeyboardAvoidingView>
    </View>
  );
}
